import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import { Input, type InputRef } from 'antd';
import * as ECT from '@whoicd/icd11ect';
import '@whoicd/icd11ect/style.css';
import classNames from 'classnames';
import { useMutation } from '@tanstack/react-query';
import styled from '@emotion/styled';

const StyledInput = styled(Input)`
  &::placeholder {
    visibility: hidden !important;
  }
`;

export type ICD11Entity = {
  iNo: string;
  linearizationUri: string;
  foundationUri: string;
  code: string;
  title: string;
  selectedText: string;
  searchQuery?: string;
};

type EmbeddedCodingToolProps = {
  value?: string;
  onChange?: (value: string) => void;
  onSelect?: (entity: ICD11Entity) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

export type EmbeddedCodingToolRef = {
  focus: () => void;
  clear: () => void;
  getValue: () => string;
  getSelectedEntity: () => ICD11Entity | null;
};

const INSTANCE_NUMBER = 1;

export const EmbeddedCodingTool = forwardRef<EmbeddedCodingToolRef, EmbeddedCodingToolProps>(
  ({ value = '', onChange, onSelect, placeholder, disabled, className }, ref) => {
    const dispatch = useDispatch();
    const [inputValue, setInputValue] = useState(value);
    const [selectedEntity, setSelectedEntity] = useState<ICD11Entity | null>(null);
    const inputRef = useRef<InputRef>(null);

    // To remove the odd 'null ...' placeholder so screen readers don't announce it
    useEffect(() => {
      const input = document.querySelector<HTMLInputElement>(`[data-ctw-ino="${INSTANCE_NUMBER}"]`);

      if (!input) return;

      const removePlaceholder = () => {
        if (!input?.placeholder?.match(/null|\./)) return;
        input.removeAttribute('placeholder');
      };

      removePlaceholder();

      const observer = new MutationObserver(removePlaceholder);
      observer.observe(input, { attributes: true, attributeFilter: ['placeholder'] });

      return () => observer.disconnect();
    }, []);

    useEffect(() => {
      setInputValue(value);
    }, [value]);

    const { mutateAsync: getNewTokenFunction } = useMutation({
      mutationFn: async () => (await dispatch(actions.getICD11Token())).access_token
    });

    useEffect(() => {
      ECT.Handler.configure(
        {
          apiServerUrl: 'https://id.who.int',
          apiSecured: true,
          autoBind: false,
          popupMode: true,
          chaptersAvailable: false,
          wordsAvailable: false
        },
        {
          getNewTokenFunction,
          selectedEntityFunction: (entity: ICD11Entity) => {
            setSelectedEntity(entity);
            onSelect?.(entity);
            ECT.Handler.clear(INSTANCE_NUMBER);
          }
        }
      );

      ECT.Handler.bind(INSTANCE_NUMBER);

      return () => {};
    }, [onSelect]);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      clear: () => {
        setInputValue('');
        setSelectedEntity(null);
        ECT.Handler.clear(INSTANCE_NUMBER);
      },
      getValue: () => inputValue,
      getSelectedEntity: () => selectedEntity
    }));

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      onChange?.(newValue);
    };

    return (
      <>
        <StyledInput
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          className={classNames(
            // 'ctw-input' is a magic classname that picks up up the @whoicd/icd11ect
            // styles
            'ctw-input',
            className
          )}
          autoComplete="off"
          data-ctw-ino={INSTANCE_NUMBER}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={disabled}
          data-testid="diagnosis-input"
          aria-label="Diagnosis: search"
        />

        <div className="ctw-window" data-ctw-ino={INSTANCE_NUMBER} />
      </>
    );
  }
);
